import { useCallback, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

/**
 * Volver a un listado tal como se dejó. Son dos mitades:
 *
 * 1. `useFiltrosUrl` guarda los filtros y cuánto se había cargado en la query
 *    string, así que la vista se reconstruye desde la URL: al abrir una ficha y
 *    volver, el navegador restaura la misma dirección y con ella lo que la
 *    persona estaba mirando. De paso el listado queda compartible y aguanta un F5.
 * 2. `useFocoLista` recuerda qué elemento se abrió, para destacarlo al volver.
 *    Eso no va en la URL: no es un filtro sino el rastro de la navegación, y
 *    vive en `sessionStorage` (por pestaña).
 *
 * Cuánto se había cargado se cuenta de dos maneras según el listado: `pagina`
 * donde se pasa página por página, y `tandas` donde el scroll infinito las va
 * apilando. Las dos viven en la URL por lo mismo: volver tiene que mostrar la
 * lista completa que se estaba mirando, o el elemento destacado no está en ella.
 */

/**
 * La pestaña abierta, en la URL (`?ver=revisiones`).
 *
 * Por lo mismo que los filtros: al entrar a una ficha y volver, el navegador restaura
 * la dirección y con ella la pestaña que se estaba mirando. Sin esto, alguien que
 * revisaba en "Revisiones" abría a un estudiante y al volver aparecía en "Mis
 * estudiantes", que no es donde estaba trabajando.
 *
 * `valores` son las claves válidas: si la URL trae otra cosa —una pestaña que ya no
 * existe, un enlace viejo, algo escrito a mano— se cae a la primera en vez de quedar
 * en una pestaña vacía.
 *
 * Reemplaza la entrada del historial, no la apila: "Volver" tiene que salir de la
 * página, no deshacer pestaña por pestaña.
 */
export function usePestanaUrl(clave: string, valores: string[], porOmision?: string) {
  const [params, setParams] = useSearchParams();
  const enUrl = params.get(clave);
  const activa = enUrl && valores.includes(enUrl) ? enUrl : (porOmision ?? valores[0] ?? "");

  const elegir = useCallback(
    (valor: string) =>
      setParams(
        (previos) => {
          // Se conservan los demás parámetros: cambiar de pestaña no puede borrar el
          // filtro ni la página en que iba la persona.
          const siguientes = new URLSearchParams(previos);
          siguientes.set(clave, valor);
          return siguientes;
        },
        { replace: true },
      ),
    [setParams, clave],
  );

  return [activa, elegir] as const;
}

/**
 * Filtros y avance del listado en la URL (`?area=3&pagina=2`, `?area=3&tandas=4`).
 *
 * `prefijo` separa dos listados de una misma página (en Temas: `mis_` para "Mis
 * temas" y sin prefijo para el catálogo).
 */
export function useFiltrosUrl(prefijo = "") {
  const [params, setParams] = useSearchParams();

  /** Valor de un filtro; `porOmision` cuando no está en la URL. */
  const leer = useCallback((clave: string, porOmision = "") => params.get(prefijo + clave) ?? porOmision, [params, prefijo]);

  /** Página actual; 1 si no está en la URL o no es un número válido. */
  const pagina = Math.max(1, Number(params.get(`${prefijo}pagina`)) || 1);

  /** Tandas cargadas en un listado de scroll infinito; 1 al entrar. */
  const tandas = Math.max(1, Number(params.get(`${prefijo}tandas`)) || 1);

  /**
   * Escribe varios parámetros de una vez; el valor vacío borra el suyo, para que
   * la URL solo lleve lo que la persona eligió.
   *
   * Reemplaza la entrada del historial en vez de apilar una nueva: "Volver" debe
   * salir del listado, no deshacer filtro por filtro.
   */
  const escribir = useCallback(
    (cambios: Record<string, string>) =>
      setParams(
        (previos) => {
          const siguientes = new URLSearchParams(previos);
          Object.entries(cambios).forEach(([clave, valor]) =>
            valor ? siguientes.set(prefijo + clave, valor) : siguientes.delete(prefijo + clave),
          );
          return siguientes;
        },
        { replace: true },
      ),
    [setParams, prefijo],
  );

  /** Cambiar un filtro vuelve al principio: lo que se había avanzado ya no aplica. */
  const filtrar = useCallback(
    (cambios: Record<string, string>) => escribir({ ...cambios, pagina: "", tandas: "" }),
    [escribir],
  );

  const irAPagina = useCallback((p: number) => escribir({ pagina: p > 1 ? String(p) : "" }), [escribir]);

  const verTandas = useCallback((n: number) => escribir({ tandas: n > 1 ? String(n) : "" }), [escribir]);

  return { leer, pagina, tandas, filtrar, irAPagina, verTandas };
}

const CLAVE_FOCO = "listas:foco";

/**
 * El elemento del que se viene, para destacarlo al volver.
 *
 * Se guarda junto a la URL del listado y solo se aplica si es la misma: volver
 * de la ficha restaura esa dirección (mismos filtros, misma página), mientras
 * que entrar al listado desde el menú llega sin ellos y no destaca nada.
 */
export function useFocoLista() {
  const { pathname, search } = useLocation();
  const url = pathname + search;
  // Se lee una sola vez, al montar: después la URL cambia con cada filtro y el
  // destacado dejaría de corresponder a lo que se estaba mirando.
  const [foco] = useState(() => leerFoco(url));

  const marcarFoco = useCallback(
    (id: number) => {
      try {
        sessionStorage.setItem(CLAVE_FOCO, JSON.stringify({ id, url }));
      } catch {
        // Sin sessionStorage (modo privado) se pierde el destacado, nada más.
      }
    },
    [url],
  );

  return { foco, marcarFoco };
}

function leerFoco(url: string): number | null {
  try {
    const guardado = JSON.parse(sessionStorage.getItem(CLAVE_FOCO) ?? "null");
    return guardado?.url === url ? Number(guardado.id) : null;
  } catch {
    return null;
  }
}
